import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ChevronRight, AlertCircle } from "lucide-react";
import { useState } from "react";

interface Aba {
  id: string;
  label: string;
  descricao: string;
  prompt: number;
}

interface ERGOSWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onTabChange: (tabId: string) => void;
  abas: Aba[];
}

export default function ERGOSWizard({ isOpen, onClose, onTabChange, abas }: ERGOSWizardProps) {
  const [currentStep, setCurrentStep] = useState(0);

  const handleNext = () => {
    if (currentStep < abas.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleGoToTab = (tabId: string) => {
    onTabChange(tabId);
    onClose();
  };

  const handleFinish = () => {
    onClose();
  };

  const aba = abas[currentStep];
  const isLastStep = currentStep === abas.length - 1;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Assistente ERGOS</DialogTitle>
          <DialogDescription>
            Configuração passo a passo da metodologia ERGOS
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Progress Indicator */}
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                {abas.map((_, index) => (
                  <div key={index} className="flex items-center">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                        index <= currentStep
                          ? "bg-[#002F59] text-white"
                          : "bg-gray-300 text-gray-600"
                      }`}
                    >
                      {index + 1}
                    </div>
                    {index < abas.length - 1 && (
                      <div
                        className={`w-8 h-1 mx-1 ${
                          index < currentStep ? "bg-[#002F59]" : "bg-gray-300"
                        }`}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
            <span className="text-sm text-muted-foreground">
              Passo {currentStep + 1} de {abas.length}
            </span>
          </div>

          {/* Conteúdo do Passo */}
          <Card className="p-6 space-y-4">
            <div>
              <h3 className="text-lg font-bold text-[#002F59]">{aba.label}</h3>
              <p className="text-sm text-muted-foreground mt-1">{aba.descricao}</p>
            </div>

            {/* Alert de Dependência */}
            {currentStep > 0 && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Esta etapa depende da publicação da etapa anterior.
                </AlertDescription>
              </Alert>
            )}

            {/* Placeholder */}
            <div className="flex flex-col items-center justify-center py-8 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
              <AlertCircle size={40} className="text-gray-400 mb-3" />
              <p className="text-sm text-gray-600 text-center">
                Será implementado no <strong>Prompt {aba.prompt}</strong>
              </p>
            </div>

            {/* Info */}
            <Alert className="bg-blue-50 border-blue-200">
              <AlertCircle className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-900">
                Publicação e validações serão implementadas nos próximos prompts.
              </AlertDescription>
            </Alert>
          </Card>

          {/* Botões de Ação */}
          <div className="flex items-center justify-between gap-3">
            <Button
              variant="outline"
              onClick={handlePrevious}
              disabled={currentStep === 0}
            >
              Anterior
            </Button>

            <Button
              variant="outline"
              onClick={() => handleGoToTab(aba.id)}
              className="flex-1"
            >
              Ir para a aba
              <ChevronRight size={16} className="ml-2" />
            </Button>

            {!isLastStep ? (
              <Button onClick={handleNext}>
                Próximo
                <ChevronRight size={16} className="ml-2" />
              </Button>
            ) : (
              <Button onClick={handleFinish} className="bg-green-600 hover:bg-green-700">
                Finalizar
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
